import { useQuery } from '@tanstack/react-query';
import { adminApi, type TestOtpEntry } from '@/lib/adminApi';
import { DataTable, Spinner, EmptyState, type Column } from '@/components/ui';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';

const columns: Array<Column<TestOtpEntry>> = [
  {
    key: 'phone',
    header: 'Test number',
    cell: (r) => <span className="metric">{r.phone}</span>,
  },
  {
    key: 'code',
    header: 'Code',
    cell: (r) => (
      <span
        className="metric"
        style={{ fontSize: 20, fontWeight: 700, letterSpacing: 3 }}
      >
        {r.code}
      </span>
    ),
  },
  {
    key: 'created_at',
    header: 'Sent',
    align: 'right',
    cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span>,
  },
];

/** OTP Codes ("God View") — live login codes for the TEST numbers so staff can
 *  read codes during testing without SSHing into the box. Only OTP_TEST_NUMBERS
 *  are recorded; a real user's code is sent by SMS and is NEVER shown here.
 *  Auto-refreshes every 4s. Gated on dashboard.read. */
export function OtpCodes() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'otp-codes'],
    queryFn: ({ signal }) => adminApi.otpCodes(signal),
    refetchInterval: 4000,
  });

  const items = data?.items ?? [];

  return (
    <AdminPage>
      <AdminPageHeader
        title="OTP Codes"
        subtitle="Live login codes for the test numbers (auto-refreshes). Real users get their code by SMS — those are never shown here."
      />
      {isLoading ? (
        <Spinner label="Loading codes…" />
      ) : isError ? (
        <EmptyState title="Couldn't load codes" description={(error as Error)?.message} />
      ) : (
        <DataTable<TestOtpEntry>
          columns={columns}
          rows={items}
          rowKey={(r) => `${r.phone}-${r.created_at}`}
          stickyHeader
          empty={
            <EmptyState
              title="No codes yet"
              description="Request an OTP on a test number — it'll show up here within a few seconds."
            />
          }
        />
      )}
    </AdminPage>
  );
}
